import { afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IGift } from '../../types';
import { BucketPipe } from '../../bucket.pipe';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../../auth.service';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { LocationService } from '../../location.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { backend } from '../../../environments/environment';

interface IDistribution {
  current: IGift[]; 
  giver_title: string; 
  giver_image?: string; 
  giver_image_url?: string;
}

interface IGiver {
  name: string;
  image?: string;
  image_url?: string;
}

@Component({
  selector: 'gift-preview',
  templateUrl: './gift-preview.component.html',
  styleUrls: ['./gift-preview.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BucketPipe, MatButtonModule, DatePipe]
})
export default class GiftPreviewComponent {
  activated = inject(ActivatedRoute);
  destroy = inject(DestroyRef);
  router = inject(LocationService);
  toast = inject(ToastrService);
  http = inject(HttpClient);

  giver = signal<IGiver|null>(null);
  /// The gift or all of the gifts nested in bundles (i.e. the gifts inside each gift, recursively) 
  gifts = signal<IGift[]>([]);
  isLoading = signal<boolean>(true);
  distributionId: string = '';
  title = computed(() => {
    const gifts = this.gifts();
    const giver = this.giver();

    if (!gifts.length) return '';
    
    let title = 'Here';

    const giftTypes = Array.from(new Set(gifts.map((g) => g.type)));
    const giftType = giftTypes[0];

    if (giftTypes.length > 1){
      // Mixed gift types
      title += ' are your items';
    } else if (gifts.length > 1) {
      // Same of the same type
      title += ' are your ' + giftType + 's';
    } else {
      // One gift
      title += ' is your ' + giftType;
    }

    if (giver?.name || giver?.image_url) {
      title += ' from';
      title += !giver.image_url ? ' ' + giver.name : '';
    }

    return title;
  });

  constructor() {
    const identifier = this.activated.snapshot.queryParams.id;

    if(identifier){
      afterNextRender(() => {
        this.http.get<IDistribution>(backend + 'distribution', { params: { identifier }}).pipe(takeUntilDestroyed(this.destroy)).subscribe({
          next: result => {
            this.gifts.set(this.flattenGifts(result.current));
            this.giver.set({
              name: result.giver_title,
              image: result.giver_image,
              image_url: result.giver_image_url
            })

            this.isLoading.set(false);

          },
          error: error => {
            error = error.error || error;
            this.toast.error(error.message);
            this.isLoading.set(false);
            this.router.navigate(['/']);
          }
        });
      });
    } else {
      this.router.navigate(['/']);
    }
  }

  flattenGifts(gifts: IGift[]){
    let flatGifts: IGift[] = [];

    for(let gift of gifts){
      if(gift.contents){
        flatGifts.push(...this.flattenGifts(gift.contents));
      } else {
        flatGifts.push(gift);
      }
    }

    return flatGifts;
  }

  claimGift(): void {
    const distributionId = this.activated.snapshot.queryParams.id;
    if(!distributionId) return;

    this.router.navigate(['/gift/redeem'], { queryParams: { id: distributionId }});
  }

}
